import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
} from "recharts";

const LABEL_MAX_CHARS = 14;
const LABEL_FONT_SIZE = 11;

function splitCampaignLabel(
  text: string,
  maxChars: number = LABEL_MAX_CHARS,
  maxLines: number = 2
): string[] {
  const t = (text || "").trim();
  if (!t) return [""];
  if (t.length <= maxChars) return [t];
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [t];
  if (maxLines >= 3 && words.length >= 3) {
    const n1 = Math.ceil(words.length / 3);
    const n2 = Math.ceil((2 * words.length) / 3);
    return [
      words.slice(0, n1).join(" "),
      words.slice(n1, n2).join(" "),
      words.slice(n2).join(" "),
    ];
  }
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
}

function getLabelGap(): number {
  if (typeof window === "undefined") return 10;
  const w = window.innerWidth;
  if (w >= 1024) return 10;
  if (w >= 768) return 8;
  return 6;
}

const ResponsiveContainerC = ResponsiveContainer as any;
const BarChartC = BarChart as any;
const CartesianGridC = CartesianGrid as any;
const XAxisC = XAxis as any;
const YAxisC = YAxis as any;
const TooltipC = Tooltip as any;
const LegendC = Legend as any;
const BarC = Bar as any;

const COLOR_TOTAL = "#818cf8";   // Light blue (Total Leads)
const COLOR_CLOSED = "#10b981";  // Green (Closed Leads)

interface AssigneeData {
  team_member: string;
  assigned_leads: number;
  closed_leads: number;
  closure_rate: number;
  assignee_id?: string | number;
  id?: string | number;
}

interface AssigneePerformanceChartProps {
  data: AssigneeData[];
  /** When provided, bars are clickable; (assigneeId, statusType, assigneeName) */
  onBarClick?: (assigneeId: string | number, statusType: "all" | "closed", assigneeName: string) => void;
}

// Modern floating tooltip with detailed metrics
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const total = payload.find((p: any) => p.dataKey === "assigned_leads")?.value ?? 0;
  const closed = payload.find((p: any) => p.dataKey === "closed_leads")?.value ?? 0;
  const rate = total > 0 ? Math.round((closed / total) * 100) : 0;

  return (
    <div
      style={{
        backgroundColor: "#fff",
        padding: "14px 18px",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e2e8f0",
        minWidth: "160px",
      }}
    >
      <div
        style={{
          fontSize: "13px",
          fontWeight: 600,
          color: "#1e293b",
          marginBottom: "12px",
          paddingBottom: "8px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        {(label ?? "").replace(/_/g, " ")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#64748b" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "2px", backgroundColor: COLOR_TOTAL }} />
            Total
          </span>
          <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>{total}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#64748b" }}>
            <span style={{ width: "8px", height: "8px", borderRadius: "2px", backgroundColor: COLOR_CLOSED }} />
            Closed
          </span>
          <span style={{ fontSize: "14px", fontWeight: 700, color: COLOR_CLOSED }}>{closed}</span>
        </div>
        <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
          Closure rate: <strong style={{ color: "#10b981" }}>{rate}%</strong>
        </div>
      </div>
    </div>
  );
};

// X-axis: same as Leads per campaign – horizontal, 1–3 lines, underscores as spaces
const CustomXAxisTick = (props: any) => {
  const { x, y, payload, dataLength } = props;
  const n = dataLength ?? 0;
  const [labelGap, setLabelGap] = useState(() => getLabelGap());
  useEffect(() => {
    const onResize = () => setLabelGap(getLabelGap());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const value = (payload?.value ?? "").trim().replace(/_/g, " ");
  const maxChars = n <= 3 ? 999 : n >= 6 ? 10 : n >= 4 ? 12 : LABEL_MAX_CHARS;
  const maxLines = n > 4 ? 3 : 2;
  const lines = splitCampaignLabel(value, maxChars, maxLines);
  const fill = "#64748b";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={labelGap}
        textAnchor="middle"
        fill={fill}
        fontSize={LABEL_FONT_SIZE}
        fontWeight={500}
        style={{ fontFamily: "inherit" }}
      >
        {lines.length === 1 ? (
          lines[0]
        ) : lines.length === 2 ? (
          <>
            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
          </>
        ) : (
          <>
            <tspan x={0} dy={0} textAnchor="middle">{lines[0]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[1]}</tspan>
            <tspan x={0} dy="1.15em" textAnchor="middle">{lines[2]}</tspan>
          </>
        )}
      </text>
    </g>
  );
};

// Legend: Closed Leads (green) first, then Total Leads (light blue) – match screenshot order
const renderLegend = (props: any) => {
  const { payload } = props;
  const order = ["Total", "Closed"];
  const sorted = [...payload].sort((a: any, b: any) => order.indexOf(a.value) - order.indexOf(b.value));
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: "24px", paddingTop: "8px", flexWrap: "wrap" }}>
      {sorted.map((entry: any, index: number) => (
        <div
          key={`legend-${index}`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "12px",
            color: "#475569",
            fontWeight: 500,
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "2px",
              backgroundColor: entry.color,
            }}
          />
          <span>{entry.value === "Total" ? "Total Leads" : "Closed Leads"}</span>
        </div>
      ))}
    </div>
  );
};

const AssigneePerformanceChart: React.FC<AssigneePerformanceChartProps> = ({ data = [], onBarClick }) => {
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "280px",
          padding: "32px",
          color: "#64748b",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <span style={{ fontSize: "24px" }}>👤</span>
        </div>
        <div style={{ fontWeight: 600, color: "#475569", marginBottom: "4px" }}>No assignee data yet</div>
        <div>Lead volume by assignee will appear here when data is available.</div>
      </div>
    );
  }

  const chartData = data.map((assignee, index) => ({
    team_member: assignee.team_member,
    assigned_leads: assignee.assigned_leads,
    closed_leads: assignee.closed_leads,
    closure_rate: assignee.closure_rate,
    assignee_id: assignee.assignee_id ?? assignee.id ?? String(index),
  }));

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "300px",
      }}
    >
      <ResponsiveContainerC width="100%" height={300}>
        <BarChartC
          data={chartData}
          margin={{
            top: 8,
            right: 52,
            bottom: chartData.length > 4 ? 64 : 56,
            left: chartData.length >= 6 ? 44 : 36,
          }}
          barCategoryGap={chartData.length >= 8 ? "32%" : chartData.length > 6 ? "30%" : chartData.length > 4 ? "28%" : "24%"}
          barGap={6}
        >
          <CartesianGridC stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
          <XAxisC
            dataKey="team_member"
            axisLine={false}
            tickLine={false}
            tick={<CustomXAxisTick dataLength={chartData.length} />}
            tickMargin={8}
            interval={0}
            height={chartData.length > 4 ? 64 : 48}
          />
          <YAxisC
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            tickFormatter={(value: number) => Number.isInteger(value) ? String(value) : String(Math.round(value))}
            tick={{ fontSize: 11, fill: "#64748b", fontWeight: 500 }}
            width={36}
          />
          <TooltipC content={<CustomTooltip />} cursor={{ fill: "rgba(100, 116, 139, 0.06)", radius: 6 }} />
          <LegendC content={renderLegend} verticalAlign="bottom" height={36} />
          <BarC
            dataKey="assigned_leads"
            name="Total"
            fill={COLOR_TOTAL}
            legendType="square"
            radius={[6, 6, 0, 0]}
            maxBarSize={36}
            animationBegin={0}
            animationDuration={600}
            animationEasing="ease-out"
            onClick={onBarClick ? (data: any) => data?.assignee_id != null && onBarClick(data.assignee_id, "all", data.team_member ?? "") : undefined}
            style={{ cursor: onBarClick ? "pointer" : undefined }}
          />
          <BarC
            dataKey="closed_leads"
            name="Closed"
            fill={COLOR_CLOSED}
            legendType="square"
            radius={[6, 6, 0, 0]}
            maxBarSize={36}
            animationBegin={100}
            animationDuration={600}
            animationEasing="ease-out"
            onClick={onBarClick ? (data: any) => data?.assignee_id != null && onBarClick(data.assignee_id, "closed", data.team_member ?? "") : undefined}
            style={{ cursor: onBarClick ? "pointer" : undefined }}
          />
        </BarChartC>
      </ResponsiveContainerC>
    </div>
  );
};

export default AssigneePerformanceChart;
